// src/pages/tricho/components/ImageStage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { clamp, normalizeRect } from "../tricho.utils";

import { Button } from "react-bootstrap";

/**
 * ImageStage (produção):
 * - Container com overflow auto (scroll aparece conforme zoom)
 * - Zoom com wheel mantendo o ponto do mouse
 * - Conteúdo fica sempre "centrado" quando menor que o viewport
 * - Marcação: click+drag cria retângulo (coords em "imagem real")
 * - Seleção/mover/redimensionar com handles
 * - Delete: tecla Delete ou botão
 *
 * marks: [{id,type,rect:{x,y,w,h},score}]
 */
export default function ImageStage({
  image,
  marks,
  selectedId,
  setSelectedId,
  onAddMark,
  onUpdateRect,
  onDeleteSelected,
  showScores,
}) {
  const containerRef = useRef(null);

  // scale altera tamanho "real" do conteúdo => scroll funciona naturalmente
  const [scale, setScale] = useState(1);

  // draft rect em coords de imagem (não escaladas)
  const [draft, setDraft] = useState(null);

  // move/resize em coords de imagem
  const [dragging, setDragging] = useState(null); // {id, start:{x,y}, origRect}
  const [resizing, setResizing] = useState(null); // {id, handle, start:{x,y}, origRect}

  // === Helpers de coordenadas ===
  const getContainerRect = () => containerRef.current?.getBoundingClientRect();

  const getPointerInContainer = (e) => {
    const rect = getContainerRect();
    if (!rect) return { x: 0, y: 0 };

    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;

    // posição dentro do container (viewport interno)
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    return { x, y };
  };

  const containerToContent = (pt) => {
    // pt dentro do viewport do container => converter para coords do conteúdo (com scroll)
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    return { x: pt.x + el.scrollLeft, y: pt.y + el.scrollTop };
  };

  const contentToImage = (pt) => {
    // conteúdo está em pixels escalados; converter para imagem real:
    return { x: pt.x / scale, y: pt.y / scale };
  };

  const imageToContent = (pt) => {
    return { x: pt.x * scale, y: pt.y * scale };
  };

  // Centraliza o conteúdo quando ele fica menor que o viewport
  const [centerPad, setCenterPad] = useState({ padX: 0, padY: 0 });

  const recalcCenterPad = () => {
    const el = containerRef.current;
    if (!el || !image) return;
    const vw = el.clientWidth;
    const vh = el.clientHeight;
    const cw = image.width * scale;
    const ch = image.height * scale;

    const padX = cw < vw ? Math.floor((vw - cw) / 2) : 0;
    const padY = ch < vh ? Math.floor((vh - ch) / 2) : 0;

    setCenterPad({ padX, padY });
  };

  // Fit inicial ao trocar imagem
  useEffect(() => {
    if (!image || !containerRef.current) return;

    const el = containerRef.current;
    const vw = el.clientWidth;
    const vh = el.clientHeight;

    // fit contain
    const s = Math.min(vw / image.width, vh / image.height);
    const fit = clamp(s, 0.1, 6);

    setScale(fit);

    // reseta scroll
    requestAnimationFrame(() => {
      el.scrollLeft = 0;
      el.scrollTop = 0;
    });
  }, [image?.src]);

  // Recalcular padding de centralização quando scale muda ou viewport muda
  useEffect(() => {
    recalcCenterPad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, image?.src]);

  useEffect(() => {
    const onResize = () => recalcCenterPad();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, image?.src]);

  // Teclas úteis
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === "Delete") onDeleteSelected?.();
      if (e.code === "Escape") setSelectedId(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onDeleteSelected, setSelectedId]);

  // === Hit test (seleção) em coords de imagem ===
  const findMarkAtImagePoint = (imgPt) => {
    for (let i = marks.length - 1; i >= 0; i--) {
      const m = marks[i];
      const r = m.rect;
      const x1 = Math.min(r.x, r.x + r.w);
      const y1 = Math.min(r.y, r.y + r.h);
      const x2 = Math.max(r.x, r.x + r.w);
      const y2 = Math.max(r.y, r.y + r.h);

      if (imgPt.x >= x1 && imgPt.x <= x2 && imgPt.y >= y1 && imgPt.y <= y2) {
        return m;
      }
    }
    return null;
  };

  // === Zoom com wheel mantendo o ponto do mouse ===
  const onWheel = (e) => {
    e.preventDefault();

    const el = containerRef.current;
    if (!el || !image) return;

    const rect = getContainerRect();
    if (!rect) return;

    // Ponto do mouse dentro do container (viewport)
    const pViewport = getPointerInContainer(e);
    // Converter para coords do conteúdo (inclui scroll)
    const pContentBefore = containerToContent(pViewport);

    // scale atual
    const prevScale = scale;

    // zoom factor
    const delta = -e.deltaY;
    const factor = delta > 0 ? 1.12 : 0.89;
    const nextScale = clamp(prevScale * factor, 0.15, 8);

    // razão de escala
    const ratio = nextScale / prevScale;

    // Após aumentar scale, o ponto do conteúdo vira:
    const pContentAfterX = pContentBefore.x * ratio;
    const pContentAfterY = pContentBefore.y * ratio;

    // Queremos manter o mesmo ponto embaixo do mouse:
    const nextScrollLeft = pContentAfterX - pViewport.x;
    const nextScrollTop = pContentAfterY - pViewport.y;

    setScale(nextScale);

    requestAnimationFrame(() => {
      // Como centralizamos com padding, precisamos considerar isso:
      recalcCenterPad();

      el.scrollLeft = nextScrollLeft;
      el.scrollTop = nextScrollTop;
    });
  };

  // Botões de zoom
  const zoomBy = (mul) => {
    const el = containerRef.current;
    if (!el) return;

    const rect = getContainerRect();
    if (!rect) return;

    // foca no centro do viewport
    const pViewport = { x: rect.width / 2, y: rect.height / 2 };
    const pContentBefore = containerToContent(pViewport);

    const prevScale = scale;
    const nextScale = clamp(prevScale * mul, 0.15, 8);
    const ratio = nextScale / prevScale;

    const pContentAfterX = pContentBefore.x * ratio;
    const pContentAfterY = pContentBefore.y * ratio;

    const nextScrollLeft = pContentAfterX - pViewport.x;
    const nextScrollTop = pContentAfterY - pViewport.y;

    setScale(nextScale);
    requestAnimationFrame(() => {
      recalcCenterPad();
      el.scrollLeft = nextScrollLeft;
      el.scrollTop = nextScrollTop;
    });
  };

  // === Interação: criar retângulo / mover / resize ===
  const onPointerDown = (e) => {
    if (!image) return;
    // permitir touch/mouse
    e.preventDefault();

    const pViewport = getPointerInContainer(e);
    const pContent = containerToContent(pViewport);

    // Ajuste pelo padding de centralização
    const pContentAdj = { x: pContent.x - centerPad.padX, y: pContent.y - centerPad.padY };
    const imgPt = contentToImage(pContentAdj);

    // se clicou numa marcação => selecionar e preparar drag
    const hit = findMarkAtImagePoint(imgPt);
    if (hit) {
      setSelectedId(hit.id);
      setDragging({
        id: hit.id,
        start: imgPt,
        origRect: { ...hit.rect },
      });
      return;
    }

    // senão cria novo draft
    setSelectedId(null);
    setDraft({ x: imgPt.x, y: imgPt.y, w: 0, h: 0 });
  };

  const onPointerMove = (e) => {
    if (!image) return;

    const pViewport = getPointerInContainer(e);
    const pContent = containerToContent(pViewport);
    const pContentAdj = { x: pContent.x - centerPad.padX, y: pContent.y - centerPad.padY };
    const imgPt = contentToImage(pContentAdj);

    if (dragging) {
      e.preventDefault();
      const dx = imgPt.x - dragging.start.x;
      const dy = imgPt.y - dragging.start.y;

      onUpdateRect?.(dragging.id, {
        x: dragging.origRect.x + dx,
        y: dragging.origRect.y + dy,
      });
      return;
    }

    if (resizing) {
      e.preventDefault();
      const dx = imgPt.x - resizing.start.x;
      const dy = imgPt.y - resizing.start.y;
      const r = resizing.origRect;

      if (resizing.handle === "nw") {
        onUpdateRect?.(resizing.id, { x: r.x + dx, y: r.y + dy, w: r.w - dx, h: r.h - dy });
      } else if (resizing.handle === "ne") {
        onUpdateRect?.(resizing.id, { y: r.y + dy, w: r.w + dx, h: r.h - dy });
      } else if (resizing.handle === "sw") {
        onUpdateRect?.(resizing.id, { x: r.x + dx, w: r.w - dx, h: r.h + dy });
      } else if (resizing.handle === "se") {
        onUpdateRect?.(resizing.id, { w: r.w + dx, h: r.h + dy });
      }
      return;
    }

    if (draft) {
      e.preventDefault();
      setDraft((d) => ({ ...d, w: imgPt.x - d.x, h: imgPt.y - d.y }));
    }
  };

  const onPointerUp = () => {
    if (dragging) setDragging(null);
    if (resizing) setResizing(null);

    if (draft) {
      const r = normalizeRect(draft);
      setDraft(null);

      if (Math.abs(r.w) < 6 || Math.abs(r.h) < 6) return;
      onAddMark?.(r);
    }
  };

  const onHandleDown = (e, id, handle) => {
    e.preventDefault();
    e.stopPropagation();

    const pViewport = getPointerInContainer(e);
    const pContent = containerToContent(pViewport);
    const pContentAdj = { x: pContent.x - centerPad.padX, y: pContent.y - centerPad.padY };
    const imgPt = contentToImage(pContentAdj);

    const mk = marks.find((m) => m.id === id);
    if (!mk) return;

    setSelectedId(id);
    setResizing({
      id,
      handle,
      start: imgPt,
      origRect: { ...mk.rect },
    });
  };

  // Render util: imagem -> svg coords (em conteúdo escalado)
  const renderMark = (m) => {
    const selected = selectedId === m.id;

    // rect em coords imagem => converter para conteúdo escalado
    const p1 = imageToContent({ x: m.rect.x, y: m.rect.y });
    const p2 = imageToContent({ x: m.rect.x + m.rect.w, y: m.rect.y + m.rect.h });

    const x = Math.min(p1.x, p2.x) + centerPad.padX;
    const y = Math.min(p1.y, p2.y) + centerPad.padY;
    const w = Math.abs(p2.x - p1.x);
    const h = Math.abs(p2.y - p1.y);

    return (
      <g key={m.id} className={selected ? "tricho-mk tricho-mk--selected" : "tricho-mk"}>
        <rect x={x} y={y} width={w} height={h} rx="8" ry="8" />

        {showScores && typeof m.score === "number" && (
          <g>
            <rect x={x} y={y - 18} width={78} height={16} rx="6" ry="6" className="tricho-score-bg" />
            <text x={x + 6} y={y - 6} className="tricho-score-text">
              {(m.score ?? 1).toFixed(2)}
            </text>
          </g>
        )}

        {selected && (
          <>
            <circle className="tricho-handle" cx={x} cy={y} r="6" onMouseDown={(e) => onHandleDown(e, m.id, "nw")} />
            <circle className="tricho-handle" cx={x + w} cy={y} r="6" onMouseDown={(e) => onHandleDown(e, m.id, "ne")} />
            <circle className="tricho-handle" cx={x} cy={y + h} r="6" onMouseDown={(e) => onHandleDown(e, m.id, "sw")} />
            <circle className="tricho-handle" cx={x + w} cy={y + h} r="6" onMouseDown={(e) => onHandleDown(e, m.id, "se")} />
          </>
        )}
      </g>
    );
  };

  const renderDraft = () => {
    if (!draft) return null;

    const p1 = imageToContent({ x: draft.x, y: draft.y });
    const p2 = imageToContent({ x: draft.x + draft.w, y: draft.y + draft.h });

    const x = Math.min(p1.x, p2.x) + centerPad.padX;
    const y = Math.min(p1.y, p2.y) + centerPad.padY;
    const w = Math.abs(p2.x - p1.x);
    const h = Math.abs(p2.y - p1.y);

    return <rect x={x} y={y} width={w} height={h} rx="8" ry="8" className="tricho-draft" />;
  };

  // Dimensões do conteúdo (em pixels escalados)
  const contentW = useMemo(() => (image ? Math.round(image.width * scale) : 0), [image, scale]);
  const contentH = useMemo(() => (image ? Math.round(image.height * scale) : 0), [image, scale]);

  return (
    <div className="tricho-stage-wrap">
      <div className="tricho-stage-tools">
        <div className="tricho-stage-hint">
          <strong>Marcar:</strong> clique e arraste • <strong>Zoom:</strong> scroll •{" "}
          <strong>Arrastar:</strong> use as barras de rolagem
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={() => zoomBy(0.9)}>
            −
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => zoomBy(1.12)}>
            +
          </Button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="tricho-stage tricho-stage--scroll"
        onWheel={onWheel}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchStart={onPointerDown}
        onTouchMove={onPointerMove}
        onTouchEnd={onPointerUp}
      >
        <div
          className="tricho-stage-inner"
          style={{
            width: contentW + centerPad.padX * 2,
            height: contentH + centerPad.padY * 2,
          }}
        >
          <img
            src={image.src}
            alt="tricho"
            draggable={false}
            className="tricho-stage-img"
            style={{
              width: contentW,
              height: contentH,
              marginLeft: centerPad.padX,
              marginTop: centerPad.padY,
            }}
          />

          <svg
            className="tricho-stage-svg"
            width={contentW + centerPad.padX * 2}
            height={contentH + centerPad.padY * 2}
          >
            {marks.map(renderMark)}
            {renderDraft()}
          </svg>
        </div>

        {selectedId && (
          <div className="tricho-stage-actions">
            <Button size="sm" variant="outline-danger" onClick={onDeleteSelected}>
              Excluir (Del)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
